<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';

import ContactsForm from 'dashboard/components-next/Contacts/ContactsForm/ContactsForm.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Flag from 'dashboard/components-next/flag/Flag.vue';
import ContactDeleteSection from 'dashboard/components-next/Contacts/ContactsCard/ContactDeleteSection.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import countries from 'shared/constants/countries';
import { dynamicTime } from 'shared/helpers/timeHelper';
import {
  formatContactPhone,
  formatContactDocument,
  formatContactDate,
  contactLastInteraction,
  pipelineStatuses,
} from './contactListPresentation';

const props = defineProps({
  id: { type: Number, required: true },
  name: { type: String, default: '' },
  email: { type: String, default: '' },
  companyId: { type: [Number, String], default: '' },
  additionalAttributes: { type: Object, default: () => ({}) },
  customAttributes: { type: Object, default: () => ({}) },
  talkviaProfile: { type: Object, default: () => ({}) },
  talkviaProposals: { type: Object, default: () => ({}) },
  phoneNumber: { type: String, default: '' },
  thumbnail: { type: String, default: '' },
  availabilityStatus: { type: String, default: null },
  lastActivityAt: { type: Number, default: null },
  blacklisted: { type: Boolean, default: false },
  isExpanded: { type: Boolean, default: false },
  isUpdating: { type: Boolean, default: false },
  isSelected: { type: Boolean, default: false },
});

const emit = defineEmits(['toggle', 'updateContact', 'showContact', 'select']);

const { t } = useI18n();

const contactsFormRef = ref(null);

const getInitialContactData = () => ({
  id: props.id,
  name: props.name,
  email: props.email,
  companyId: props.companyId,
  phoneNumber: props.phoneNumber,
  additionalAttributes: props.additionalAttributes,
  customAttributes: props.customAttributes,
});

const contactData = ref(getInitialContactData());

const isFormInvalid = computed(() => contactsFormRef.value?.isFormInvalid);

const countriesMap = computed(() =>
  countries.reduce((acc, country) => {
    acc[country.id] = country;
    return acc;
  }, {})
);

const countryDetails = computed(() => {
  const { country, countryCode, city } = props.additionalAttributes || {};
  if (!country && !countryCode) return null;
  const activeCountry =
    countriesMap.value[country] || countriesMap.value[countryCode];
  if (!activeCountry) return null;
  return { countryCode: activeCountry.id, city, name: activeCountry.name };
});

const formattedLocation = computed(() =>
  countryDetails.value
    ? [countryDetails.value.city, countryDetails.value.name]
        .filter(Boolean)
        .join(', ')
    : ''
);

const companyName = computed(() => props.additionalAttributes?.companyName);
const phone = computed(() => formatContactPhone(props.phoneNumber));
const profile = computed(() => props.talkviaProfile || {});
const contactDocument = computed(() =>
  formatContactDocument(profile.value.cpf)
);
const birthDate = computed(() => formatContactDate(profile.value.birthDate));

const documentDetails = computed(() =>
  [
    profile.value.age &&
      t('CONTACTS_LAYOUT.LIST.YEARS', { age: profile.value.age }),
    birthDate.value,
  ]
    .filter(Boolean)
    .join(' · ')
);

const lastInteraction = computed(() =>
  contactLastInteraction(props.lastActivityAt, profile.value.lastInteractionAt)
);
const lastInteractionRelative = computed(() =>
  lastInteraction.value
    ? dynamicTime(lastInteraction.value.getTime() / 1000)
    : ''
);
const lastInteractionFull = computed(() =>
  lastInteraction.value
    ? lastInteraction.value.toLocaleString('pt-BR', {
        dateStyle: 'short',
        timeStyle: 'short',
      })
    : ''
);

const pipelineTotal = computed(() => props.talkviaProposals?.total || 0);
const pipeline = computed(() => pipelineStatuses(props.talkviaProposals));

const moneyFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

const formatMoney = value => {
  if (value === null || value === undefined || value === '') return '-';
  const numeric = Number(String(value).replace(/\./g, '').replace(',', '.'));
  return Number.isNaN(numeric) ? value : moneyFormatter.format(numeric);
};

const formatDateTime = value =>
  value ? new Date(value).toLocaleString('pt-BR') : '-';

const expandedProfileItems = computed(() =>
  [
    profile.value.age && {
      label: t('CONTACTS_LAYOUT.LIST.AGE'),
      value: profile.value.age,
    },
    birthDate.value && {
      label: t('CONTACTS_LAYOUT.LIST.BIRTH_DATE'),
      value: birthDate.value,
    },
    contactDocument.value && {
      label: t('CONTACTS_LAYOUT.LIST.CPF'),
      value: contactDocument.value,
    },
    profile.value.rg && {
      label: t('CONTACTS_LAYOUT.LIST.RG'),
      value: profile.value.rg,
    },
    profile.value.motherName && {
      label: t('CONTACTS_LAYOUT.LIST.MOTHER'),
      value: profile.value.motherName,
    },
    profile.value.registrationNumber && {
      label: t('CONTACTS_LAYOUT.LIST.REGISTRATION'),
      value: profile.value.registrationNumber,
    },
    profile.value.availableMargin && {
      label: t('CONTACTS_LAYOUT.LIST.MARGIN'),
      value: formatMoney(profile.value.availableMargin),
    },
    lastInteractionFull.value && {
      label: t('CONTACTS_LAYOUT.LIST.LAST_INTERACTION'),
      value: lastInteractionFull.value,
    },
  ].filter(Boolean)
);

const latestProposals = computed(() => props.talkviaProposals?.latest || []);

const handleFormUpdate = updatedData => {
  Object.assign(contactData.value, updatedData);
};

const handleUpdateContact = () => {
  emit('updateContact', contactData.value);
};

const handleRemoveBlacklist = () => {
  emit('updateContact', { id: props.id, blacklisted: false });
};

const onClickExpand = () => {
  emit('toggle');
  contactData.value = getInitialContactData();
};

const onClickViewDetails = () => emit('showContact', props.id);

// The whole row opens the details, except its own controls.
const onRowClick = event => {
  if (event.target.closest('button, a, input, label, [data-row-ignore]'))
    return;
  onClickViewDetails();
};
</script>

<template>
  <article
    class="group relative overflow-hidden rounded-xl border bg-n-solid-1 transition-[border-color,background-color,box-shadow] duration-200"
    :class="
      isSelected
        ? 'border-n-brand/50 bg-n-brand/5'
        : 'border-n-weak dark:border-n-strong hover:border-n-slate-6 hover:shadow-sm'
    "
  >
    <div
      class="grid cursor-pointer grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-3 gap-y-2 px-3 py-3 sm:px-4 xl:grid-cols-[2rem_minmax(0,2.2fr)_minmax(0,2fr)_minmax(0,1.3fr)_minmax(0,1.2fr)_minmax(0,1.3fr)_6rem] xl:gap-x-4"
      @click="onRowClick"
    >
      <label
        class="flex size-8 items-center justify-center rounded-lg hover:bg-n-alpha-2"
        @click.stop
      >
        <span class="sr-only">
          {{ t('CONTACTS_LAYOUT.LIST.SELECT_CONTACT', { name }) }}
        </span>
        <Checkbox
          :model-value="isSelected"
          @change="event => emit('select', event.target.checked)"
        />
      </label>

      <!-- Identity -->
      <div class="flex min-w-0 items-center gap-3">
        <Avatar
          :name="name"
          :src="thumbnail"
          :size="36"
          :status="availabilityStatus"
          rounded-full
          hide-offline-status
        />
        <div class="min-w-0">
          <button
            type="button"
            class="block max-w-full truncate p-0 text-left text-sm font-semibold text-n-slate-12 hover:text-n-brand focus-visible:text-n-brand focus-visible:outline-none"
            :title="name"
            :aria-label="t('CONTACTS_LAYOUT.LIST.OPEN_DETAILS', { name })"
            @click="onClickViewDetails"
          >
            {{ name }}
          </button>
          <p
            v-if="companyName || blacklisted"
            class="mb-0 mt-0.5 flex min-w-0 items-center gap-2 text-xs text-n-slate-11"
          >
            <span
              v-if="blacklisted"
              class="inline-flex shrink-0 items-center gap-1 rounded-md bg-n-ruby-3 px-1.5 py-0.5 text-[11px] font-medium text-n-ruby-11"
            >
              <span class="i-hugeicons-user-block-01 size-3" />
              {{ t('CONTACTS_LAYOUT.LIST.BLACKLIST') }}
            </span>
            <span v-if="companyName" class="flex min-w-0 items-center gap-1">
              <span class="i-hugeicons-building-03 size-3.5 shrink-0" />
              <span class="truncate">{{ companyName }}</span>
            </span>
          </p>
          <p
            v-if="phone"
            class="mb-0 mt-0.5 truncate text-xs text-n-slate-11 xl:hidden"
          >
            {{ phone }}
          </p>
        </div>
      </div>

      <!-- Reach -->
      <div
        class="col-span-2 col-start-2 row-start-2 flex min-w-0 flex-col gap-0.5 text-sm xl:col-span-1 xl:col-start-auto xl:row-start-auto"
      >
        <span
          v-if="phone"
          class="hidden min-w-0 items-center gap-1.5 text-n-slate-12 xl:flex"
        >
          <span class="i-hugeicons-call-02 size-3.5 shrink-0 text-n-slate-10" />
          <span class="truncate">{{ phone }}</span>
        </span>
        <span
          v-if="email"
          class="flex min-w-0 items-center gap-1.5 text-n-slate-11"
          :title="email"
        >
          <span class="i-hugeicons-mail-01 size-3.5 shrink-0 text-n-slate-10" />
          <span class="truncate">{{ email }}</span>
        </span>
        <span
          v-if="formattedLocation"
          class="flex min-w-0 items-center gap-1.5 text-xs text-n-slate-11"
        >
          <Flag :country="countryDetails.countryCode" class="size-3.5" />
          <span class="truncate">{{ formattedLocation }}</span>
        </span>
        <span
          v-if="!phone && !email && !formattedLocation"
          class="hidden text-xs text-n-slate-10 xl:inline"
        >
          {{ t('CONTACTS_LAYOUT.LIST.NO_REACH') }}
        </span>
      </div>

      <!-- Document -->
      <div
        class="col-span-2 col-start-2 flex min-w-0 flex-wrap items-baseline gap-x-2 xl:col-span-1 xl:col-start-auto xl:flex-col xl:gap-0.5"
        :class="{ 'hidden xl:flex': !contactDocument && !documentDetails }"
      >
        <span
          v-if="contactDocument"
          class="whitespace-nowrap text-sm tabular-nums text-n-slate-12"
          :title="t('CONTACTS_LAYOUT.LIST.CPF')"
        >
          {{ contactDocument }}
        </span>
        <span v-else class="text-xs text-n-slate-10">
          {{ t('CONTACTS_LAYOUT.LIST.NO_DOCUMENT') }}
        </span>
        <span v-if="documentDetails" class="text-xs text-n-slate-11">
          {{ documentDetails }}
        </span>
      </div>

      <!-- Last interaction -->
      <div
        class="col-span-2 col-start-2 flex min-w-0 items-center gap-1.5 text-xs text-n-slate-11 xl:col-span-1 xl:col-start-auto xl:flex-col xl:items-start xl:gap-0.5"
        :class="{ 'hidden xl:flex': !lastInteraction }"
      >
        <template v-if="lastInteraction">
          <span
            class="i-hugeicons-clock-01 size-3.5 shrink-0 text-n-slate-10 xl:hidden"
          />
          <span
            class="text-sm text-n-slate-12 first-letter:uppercase"
            :title="lastInteractionFull"
          >
            {{ lastInteractionRelative }}
          </span>
          <span class="hidden whitespace-nowrap tabular-nums xl:inline">
            {{ lastInteractionFull }}
          </span>
        </template>
        <span v-else class="text-n-slate-10">
          {{ t('CONTACTS_LAYOUT.LIST.NO_INTERACTION') }}
        </span>
      </div>

      <!-- Pipeline -->
      <div
        class="col-span-2 col-start-2 flex min-w-0 flex-wrap items-center gap-1.5 xl:col-span-1 xl:col-start-auto"
        :class="{ 'hidden xl:flex': !pipelineTotal }"
      >
        <template v-if="pipelineTotal">
          <span
            class="inline-flex items-center gap-1 rounded-md bg-n-alpha-2 px-1.5 py-0.5 text-xs font-medium text-n-slate-12"
          >
            <span class="i-hugeicons-legal-document-01 size-3.5" />
            {{
              t(
                'CONTACTS_LAYOUT.LIST.CONTRACTS',
                { count: pipelineTotal },
                pipelineTotal
              )
            }}
          </span>
          <span
            v-for="status in pipeline"
            :key="status.key"
            class="inline-flex items-center gap-1 text-xs text-n-slate-11"
          >
            <span class="size-1.5 rounded-full" :class="status.tone" />
            {{
              t(
                `CONTACTS_LAYOUT.LIST.${status.key}`,
                { count: status.count },
                status.count
              )
            }}
          </span>
        </template>
        <span v-else class="text-xs text-n-slate-10">
          {{ t('CONTACTS_LAYOUT.LIST.NO_PIPELINE') }}
        </span>
      </div>

      <!-- Actions -->
      <div
        class="col-start-3 row-start-1 flex items-center justify-end gap-1 xl:col-start-auto xl:row-start-auto"
      >
        <Button
          v-if="blacklisted"
          v-tooltip.top="t('CONTACTS_LAYOUT.LIST.REMOVE_BLACKLIST')"
          ghost
          slate
          xs
          icon="i-hugeicons-user-block-01"
          :aria-label="t('CONTACTS_LAYOUT.LIST.REMOVE_BLACKLIST')"
          :disabled="isUpdating"
          @click="handleRemoveBlacklist"
        />
        <Button
          v-tooltip.top="
            isExpanded
              ? t('CONTACTS_LAYOUT.LIST.CLOSE_QUICK_EDIT')
              : t('CONTACTS_LAYOUT.LIST.QUICK_EDIT')
          "
          ghost
          slate
          xs
          icon="i-hugeicons-pencil-edit-02"
          :class="{ '!bg-n-alpha-2 !text-n-brand': isExpanded }"
          :aria-label="
            isExpanded
              ? t('CONTACTS_LAYOUT.LIST.CLOSE_QUICK_EDIT')
              : t('CONTACTS_LAYOUT.LIST.QUICK_EDIT')
          "
          :aria-expanded="isExpanded"
          @click="onClickExpand"
        />
        <Button
          v-tooltip.top="t('CONTACTS_LAYOUT.CARD.VIEW_DETAILS')"
          ghost
          slate
          xs
          icon="i-hugeicons-arrow-right-01"
          :aria-label="t('CONTACTS_LAYOUT.LIST.OPEN_DETAILS', { name })"
          @click="onClickViewDetails"
        />
      </div>
    </div>

    <div
      class="grid transition-all duration-300 ease-out motion-reduce:transition-none"
      :class="
        isExpanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
      "
      data-row-ignore
    >
      <div class="overflow-hidden">
        <div
          v-if="isExpanded"
          class="flex flex-col gap-5 border-t border-n-weak bg-n-alpha-1 p-4 sm:p-5"
        >
          <section v-if="expandedProfileItems.length" class="grid gap-3">
            <h3 class="mb-0 text-sm font-semibold text-n-slate-12">
              {{ t('CONTACTS_LAYOUT.LIST.PROFILE_TITLE') }}
            </h3>
            <dl
              class="mb-0 grid gap-2 sm:grid-cols-2 lg:grid-cols-4 2xl:grid-cols-6"
            >
              <div
                v-for="item in expandedProfileItems"
                :key="item.label"
                class="min-w-0 rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2"
              >
                <dt class="text-xs text-n-slate-10">{{ item.label }}</dt>
                <dd class="mb-0 truncate text-sm font-medium text-n-slate-12">
                  {{ item.value }}
                </dd>
              </div>
            </dl>
          </section>

          <section v-if="latestProposals.length" class="grid gap-3">
            <h3 class="mb-0 text-sm font-semibold text-n-slate-12">
              {{ t('CONTACTS_LAYOUT.LIST.LATEST_CONTRACTS') }}
            </h3>
            <div class="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
              <article
                v-for="proposal in latestProposals"
                :key="proposal.id"
                class="min-w-0 rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2"
              >
                <div class="flex items-start justify-between gap-3">
                  <p class="mb-0 truncate text-sm font-medium text-n-slate-12">
                    {{
                      proposal.title ||
                      `${t('CONTACTS_LAYOUT.LIST.PROPOSAL')} #${proposal.id}`
                    }}
                  </p>
                  <span class="shrink-0 text-xs text-n-slate-10">
                    #{{ proposal.id }}
                  </span>
                </div>
                <p class="mb-0 mt-1 text-sm text-n-slate-11">
                  {{ formatMoney(proposal.amount) }} ·
                  {{ proposal.status || '-' }}
                </p>
                <p class="mb-0 text-xs text-n-slate-10">
                  {{ formatDateTime(proposal.updatedAt) }}
                </p>
              </article>
            </div>
          </section>

          <section class="grid gap-3">
            <h3 class="mb-0 text-sm font-semibold text-n-slate-12">
              {{ t('CONTACTS_LAYOUT.LIST.EDIT_TITLE') }}
            </h3>
            <ContactsForm
              ref="contactsFormRef"
              :contact-data="contactData"
              @update="handleFormUpdate"
            />
            <div>
              <Button
                :label="
                  t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.UPDATE_BUTTON')
                "
                size="sm"
                :is-loading="isUpdating"
                :disabled="isUpdating || isFormInvalid"
                @click="handleUpdateContact"
              />
            </div>
          </section>
        </div>
        <ContactDeleteSection
          v-if="isExpanded"
          :selected-contact="{ id: props.id, name: props.name }"
        />
      </div>
    </div>
  </article>
</template>
